<template>
  <div 
    class="backdrop-blur-sm rounded-xl p-6 shadow-lg transition-all duration-300 relative h-full flex flex-col" 
    :class="[bgClass, {'hover:-translate-y-2': !popular, 'hover:-translate-y-3': popular}]"
  >
    <!-- Popular badge with improved styling and animations -->
    <Motion
      v-if="popular" 
      :initial="{ opacity: 0, y: -20 }"
      :animate="{ opacity: 1, y: 0 }"
      :transition="{ duration: 0.5, delay: 0.3 }"
      class="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-md"
    >
      <div class="flex items-center gap-1">
        <v-icon size="16" color="white">mdi-star</v-icon>
        Most Popular
      </div>
    </Motion>
    
    <!-- Decorative elements -->
    <div class="absolute -right-2 -top-2 w-20 h-20 text-yellow-300/20  transform rotate-12 pointer-events-none">
      <v-icon size="64">mdi-duck</v-icon>
    </div>
    
    <div class="text-center mb-8 relative z-10">
      <h3 class="text-xl font-bold mb-3 text-slate-800 ">
        {{ title }}
      </h3>
      <div class="relative inline-block">
        <Text3d 
          class="text-4xl font-bold text-yellow-500 "
          :animate="popular"
        >{{ price }}</Text3d>
        
        <!-- Price highlight for popular plan -->
        <div v-if="popular" class="absolute -inset-2 bg-gradient-to-r from-yellow-400/20 to-amber-400/20 rounded-full blur-xl -z-10"></div>
      </div>
      <p class="text-gray-600  mt-1">{{ subtitle }}</p>
    </div>
    
    <!-- Features list with improved styling -->
    <ul class="space-y-4 mb-8 flex-grow">
      <Motion
        v-for="(feature, index) in features" 
        :key="index"
        :initial="{ opacity: 0, x: -10 }"
        :animate="{ opacity: 1, x: 0 }"
        :transition="{ duration: 0.3, delay: 0.1 * index }"
        class="flex items-start gap-3"
      >
        <div class="mt-0.5 flex-shrink-0">
          <div class="flex items-center justify-center w-5 h-5 rounded-full bg-green-100 ">
            <v-icon size="14" color="green-darken-1" class="">mdi-check</v-icon>
          </div>
        </div>
        <span class="text-gray-700  text-left">{{ feature }}</span>
      </Motion>
    </ul>
    
    <!-- Action button with enhanced styling -->
    <div class="mt-auto pt-4">
      <!-- ShimmerButton for popular plan -->
      <ShimmerButton
        v-if="popular"
        shimmer-color="rgba(255, 255, 255, 0.8)"
        background="#eab308"
        border-radius="8px"
        class="w-full py-3 font-medium text-white shadow-md"
        @click="handlePurchase"
      >
        <span v-if="isLoading">
          <v-icon class="mr-2" size="small">mdi-loading mdi-spin</v-icon>
          Processing...
        </span>
        <span v-else class="flex items-center justify-center gap-2">
          <v-icon size="18">mdi-check-decagram</v-icon>
          {{ buttonText }}
        </span>
      </ShimmerButton>
      
      <!-- Regular button for other plans -->
      <v-btn 
        v-else
        block
        color="primary" 
        size="large" 
        :variant="mappedVariant"
        :loading="isLoading"
        @click="handlePurchase"
        class="py-6"
      >
        <span class="flex items-center gap-2">
          <v-icon size="18">mdi-duck</v-icon>
          {{ buttonText }}
        </span>
      </v-btn>
    </div>
    
    <!-- Bottom security message -->
    <p class="text-xs text-center mt-4 text-gray-500  flex items-center justify-center gap-1">
      <v-icon size="14">mdi-shield-check</v-icon>
      <span>Secure payment</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { Motion } from 'motion-v';
import { Text3d } from '@/components/ui/text3d';
import { ShimmerButton } from '@/components/ui/shimmerButton';
import { computed, ref } from 'vue';
import { useAuthStore } from '@/stores/authStore';

// Define allowed variants for v-btn
type VBtnVariant = 'flat' | 'text' | 'elevated' | 'tonal' | 'outlined' | 'plain';

const props = defineProps<{
  packageId: string;
  title: string;
  price: string;
  subtitle: string;
  features: string[];
  popular: boolean;
  buttonText: string;
  buttonVariant: string;
  bgClass: string;
}>();

const emit = defineEmits(['purchase-initiated', 'login-required', 'show-cruise-form']);

const authStore = useAuthStore();
const isLoading = ref(false);

// Map the provided buttonVariant to a valid Vuetify variant
const mappedVariant = computed<VBtnVariant>(() => {
  // List of valid v-btn variants
  const validVariants: VBtnVariant[] = ['flat', 'text', 'elevated', 'tonal', 'outlined', 'plain'];
  
  // Return the variant if valid, otherwise default to 'text'
  return validVariants.includes(props.buttonVariant as VBtnVariant) 
    ? props.buttonVariant as VBtnVariant 
    : 'text';
});

const handlePurchase = async () => {
  // First check if the user is authenticated
  if (!authStore.isAuthenticated()) {
    emit('login-required');
    return;
  }
  
  // Additionally check if token is valid (not expired)
  if (!authStore.isTokenValid()) {
    // Force logout if token is invalid
    authStore.logout();
    emit('login-required');
    return;
  }
  
  try {
    isLoading.value = true;
    emit('purchase-initiated', props.packageId);
    
    // Show cruise details dialog
    emit('show-cruise-form', {
      packageId: props.packageId,
      packageTitle: props.title,
      packagePrice: props.price
    });
  } catch (error) {
    console.error('Failed to initiate purchase:', error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.hover\:scale-102:hover {
  transform: scale(1.02);
  transition: transform 0.2s ease-in-out;
}

.hover\:-translate-y-2:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.hover\:-translate-y-3:hover {
  transform: translateY(-12px);
  box-shadow: 0 20px 40px rgba(234, 179, 8, 0.2);
  transition: all 0.3s ease;
}

/* Enhanced loading animation for the button */
.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>